import React from 'react';

export type FunnelTone = 'base' | 'good' | 'warn' | 'bad';

export interface FunnelStepView {
  label: string;
  count: number;
  /** 0..1 fraction of the base — drives the bar width. */
  pct: number;
  tone?: FunnelTone;
}

const toneColor: Record<FunnelTone, string> = {
  base: 'var(--text-muted)',
  good: 'var(--green-solid)',
  warn: 'var(--amber-solid)',
  bad: 'var(--red-solid)',
};

/**
 * Horizontal shrinking-bar funnel: each row is a filter stage, the bar width is
 * its share of the eligible base, and the right column shows count + percent.
 * Presentational — the caller decides tones.
 */
export function FunnelBars({ steps }: { steps: FunnelStepView[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 150,
              flex: 'none',
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={s.label}
          >
            {s.label}
          </span>
          <span
            style={{
              flex: 1,
              height: 20,
              borderRadius: 5,
              background: 'var(--bg-muted)',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                height: '100%',
                width: `${Math.max(1, Math.min(100, s.pct * 100))}%`,
                background: toneColor[s.tone ?? 'base'],
                borderRadius: 5,
                transition: 'width .2s',
              }}
            />
          </span>
          <span
            style={{
              width: 92,
              flex: 'none',
              textAlign: 'right',
              fontSize: 'var(--fs-xs)',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--text)',
            }}
          >
            {s.count.toLocaleString('en-US')}{' '}
            <span style={{ color: 'var(--text-faint)' }}>
              {Math.round(s.pct * 100)}%
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}
